import React from 'react';
import { Box, IconButton } from '@mui/material';
import { Pause } from '@mui/icons-material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';

interface RecordingFABProps {
  isRecording: boolean;
  isPaused: boolean;
  duration: number; // in milliseconds
  hasStartedRecording: boolean;
  isStartingRecording?: boolean; // New: indicates preparing/connecting state
  onStartRecording: () => void;
  onPause: () => void;
  onResume: () => void;
  onGenerateSummary: () => void;
}

/**
 * Format time from milliseconds to MM:SS or HH:MM:SS format
 * Same format as used in the main recording controls
 */
const formatTime = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

export const RecordingFAB: React.FC<RecordingFABProps> = ({
  isRecording,
  isPaused,
  duration,
  hasStartedRecording,
  isStartingRecording = false,
  onStartRecording,
  onPause,
  onResume,
  onGenerateSummary
}) => {
  const handleMainButtonClick = () => {
    // Don't allow clicks while preparing
    if (isStartingRecording) return;
    
    if (!hasStartedRecording) {
      onStartRecording();
    } else if (isPaused) {
      onResume();
    } else {
      onPause();
    }
  };

  // no-op

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 80, // 64px for bottom nav + 16px spacing
        right: 24, // Moved left to avoid scrollbar overlap
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1
      }}
    >
      {/* Generate Summary Button (only visible when paused) */}
      {hasStartedRecording && isPaused && (
        <Box
          sx={{
            opacity: 1,
            transform: 'scale(1)',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
        <IconButton
          onClick={onGenerateSummary}
          aria-label="Generate summary"
          sx={{
            width: 56,
            height: 56,
            backgroundColor: '#2563eb',
            color: 'white',
            borderRadius: '50%', // Circular to match main FAB
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
            '&:hover': {
              backgroundColor: '#1d4ed8'
            },
            transition: 'background-color 200ms'
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 26 }} />
        </IconButton>
        </Box>
      )}

      {/* Timer Badge (only visible when recording has started) */}
      {hasStartedRecording && (
        <Box
          aria-live="polite"
          aria-label={`Recording time: ${formatTime(duration)}`}
          sx={{
            backgroundColor: '#1e293b',
            color: 'white',
            px: 1.5,
            py: 0.75,
            borderRadius: '999px',
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 600,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: '60px',
            textAlign: 'center'
          }}
        >
          {formatTime(duration)}
        </Box>
      )}

      {/* Main FAB Button */}
      <Box
        sx={{
          position: 'relative',
          width: 56,
          height: 56
        }}
      >
        {/* Animated ping ring (only when recording) */}
        {isRecording && !isPaused && (
          <Box
            sx={{
              position: 'absolute',
              inset: -2,
              borderRadius: '50%', // Circular
              border: '2px solid #ef4444',
              animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
              '@keyframes ping': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1
                },
                '75%, 100%': {
                  transform: 'scale(1.5)',
                  opacity: 0
                }
              }
            }}
          />
        )}

        {/* Main button */}
        <IconButton
          onClick={handleMainButtonClick}
          disabled={isStartingRecording}
          aria-label={
            isStartingRecording
              ? 'Preparing to record...'
              : !hasStartedRecording
              ? 'Start recording'
              : isPaused
              ? 'Resume recording'
              : 'Pause recording'
          }
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%', // Circular
            backgroundColor: isStartingRecording ? '#9ca3af' : '#ef4444', // Gray when preparing, red otherwise
            color: 'white',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: 1,
            '&:hover': {
              backgroundColor: isStartingRecording ? '#9ca3af' : '#dc2626' // Gray when preparing, darker red on hover
            },
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&.Mui-disabled': {
              backgroundColor: '#9ca3af',
              color: 'white',
              opacity: 0.8
            }
          }}
        >
          {isStartingRecording ? (
            // Loading spinner when preparing
            <Box
              sx={{
                width: 24,
                height: 24,
                border: '3px solid rgba(255, 255, 255, 0.3)',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
          ) : !hasStartedRecording ? (
            // Record dot icon when not started
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'white'
              }}
            />
          ) : isPaused ? (
            // Record dot when paused (to resume/continue)
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'white'
              }}
            />
          ) : (
            // Pause icon when recording
            <Pause sx={{ fontSize: 28 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default RecordingFAB;
